import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Separator } from "@/components/ui/separator"
import AppLayout from "@/layouts/AppLayout"
import { formatDate } from "@/lib/utils"
import { Head, router, usePage } from "@inertiajs/react"
import { IconArrowLeft, IconPencil, IconTrash } from "@tabler/icons-react"
import { useState } from "react"

interface StatusAction {
  name: string
  action: string
  color: string
}

interface Bill {
  id: number
  bill_number: string
  display_name: string
  bill_type: string
  bill_type_display: string
  billing_frequency: string
  billing_frequency_display: string
  status: string
  bill_date: string | null
  due_date: string | null
  date_paid: string | null
  currency: string
  currency_symbol: string
  subtotal: number
  tax_amount: number
  total_amount: number
  vendor_name: string | null
  description: string | null
  notes: string | null
  sent_to_accounting: boolean
  employee: { id: number; name: string } | null
  can_update: boolean
  can_destroy: boolean
  available_actions: StatusAction[]
}

interface Props {
  [key: string]: unknown
  bill: Bill
}

const STATUS_BADGE: Record<string, { variant: "default" | "secondary" | "destructive" | "outline"; label: string }> = {
  paid: { variant: "default", label: "Paid" },
  pending: { variant: "secondary", label: "Pending" },
  overdue: { variant: "destructive", label: "Overdue" },
  draft: { variant: "outline", label: "Draft" },
  cancelled: { variant: "outline", label: "Cancelled" },
}

function formatCurrency(amount: number, symbol: string) {
  return `${symbol}${amount.toFixed(2)}`
}

function actionVariant(color: string) {
  if (color === "green") return "default" as const
  if (color === "red") return "destructive" as const
  return "outline" as const
}

export default function BillShow() {
  const { bill } = usePage<Props>().props
  const badge = STATUS_BADGE[bill.status] || { variant: "outline" as const, label: bill.status }
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteProcessing, setDeleteProcessing] = useState(false)

  function handleAction(action: string) {
    router.patch(`/bills/${bill.id}/${action}`)
  }

  function handleDelete() {
    setDeleteProcessing(true)
    router.delete(`/bills/${bill.id}`, {
      onFinish: () => setDeleteProcessing(false),
    })
  }

  return (
    <>
      <Head title={`${bill.bill_number} - Operations Center`} />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        {/* Header */}
        <div className="space-y-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/bills">Bills</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{bill.bill_number}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                <a href="/bills">
                  <IconArrowLeft className="h-4 w-4" />
                </a>
              </Button>
              <div>
                <div className="flex items-center gap-2.5">
                  <h1 className="text-lg font-semibold tracking-tight">{bill.bill_number}</h1>
                  <Badge variant={badge.variant}>{badge.label}</Badge>
                </div>
                <p className="text-muted-foreground text-sm">
                  {bill.display_name} &middot; {bill.bill_type_display}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {bill.can_update &&
                bill.available_actions.map((action) => (
                  <Button
                    key={action.action}
                    variant={actionVariant(action.color)}
                    size="sm"
                    onClick={() => handleAction(action.action)}
                  >
                    {action.name}
                  </Button>
                ))}
              {bill.can_update && (
                <Button size="sm" variant="outline" asChild>
                  <a href={`/bills/${bill.id}/edit`}>
                    <IconPencil className="mr-1.5 h-4 w-4" />
                    Edit
                  </a>
                </Button>
              )}
              {bill.can_destroy && (
                <Button size="sm" variant="destructive" onClick={() => setShowDeleteModal(true)}>
                  <IconTrash className="mr-1.5 h-4 w-4" />
                  Delete
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Details Grid */}
        <div className="rounded-xl border bg-card">
          <div className="grid grid-cols-2 gap-px sm:grid-cols-3 lg:grid-cols-4">
            <DetailCell label="Bill Date" value={formatDate(bill.bill_date)} />
            <DetailCell label="Due Date" value={formatDate(bill.due_date)} />
            <DetailCell label="Date Paid" value={formatDate(bill.date_paid)} />
            <DetailCell label="Currency" value={bill.currency.toUpperCase()} />
          </div>

          <Separator />

          <div className="grid grid-cols-2 gap-px sm:grid-cols-3 lg:grid-cols-4">
            <DetailCell label="Type" value={bill.bill_type_display} />
            <DetailCell label="Frequency" value={bill.billing_frequency_display} />
            <DetailCell label="Accounting" value={bill.sent_to_accounting ? "Sent" : "Not sent"} />
            {bill.employee && <DetailCell label="Employee" value={bill.employee.name} />}
          </div>

          <Separator />

          {/* Totals */}
          <div className="flex justify-end p-5">
            <dl className="w-full max-w-xs space-y-1.5 text-sm">
              <TotalRow label="Subtotal" value={formatCurrency(bill.subtotal, bill.currency_symbol)} />
              <TotalRow label="Taxes" value={formatCurrency(bill.tax_amount, bill.currency_symbol)} muted />
              <Separator className="!my-2" />
              <div className="flex items-center justify-between font-semibold">
                <dt>Total</dt>
                <dd className="tabular-nums">{formatCurrency(bill.total_amount, bill.currency_symbol)}</dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Description & Notes */}
        {(bill.description || bill.notes) && (
          <div className="rounded-xl border bg-card">
            {bill.description && (
              <div className="p-5">
                <p className="text-muted-foreground mb-1 text-xs font-medium uppercase tracking-wider">Description</p>
                <p className="text-sm leading-relaxed">{bill.description}</p>
              </div>
            )}
            {bill.description && bill.notes && <Separator />}
            {bill.notes && (
              <div className="p-5">
                <p className="text-muted-foreground mb-1 text-xs font-medium uppercase tracking-wider">Notes</p>
                <p className="text-sm leading-relaxed">{bill.notes}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Bill</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {bill.bill_number}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteProcessing}>
              <IconTrash className="mr-2 h-4 w-4" />
              {deleteProcessing ? "Deleting..." : "Delete Bill"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function DetailCell({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="p-5">
      <p className="text-muted-foreground mb-1 text-xs font-medium uppercase tracking-wider">{label}</p>
      <p className={`text-sm tabular-nums ${bold ? "text-lg font-semibold" : "font-medium"}`}>{value}</p>
    </div>
  )
}

function TotalRow({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <dt className={muted ? "text-muted-foreground" : ""}>{label}</dt>
      <dd className={`tabular-nums ${muted ? "text-muted-foreground" : ""}`}>{value}</dd>
    </div>
  )
}

BillShow.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
